<template>
  <nav
    class="breadcrumbs"
    aria-label="Breadcrumb"
  >
    <ol class="breadcrumbs__list">
      <li class="breadcrumbs__item breadcrumbs__item--home">
        <Button
          icon="pi pi-home"
          aria-label="Ir al inicio"
          text
          raised
          rounded
          outlined
          severity="secondary"
          class="breadcrumbs__item__icon"
          tabindex="0"
          @click="goToHome"
          @keyup.enter="goToHome"
        />
      </li>

      <li
        v-for="(breadCrumb, index) in appState$$?.breadCrumbs"
        :key="breadCrumb"
        class="breadcrumbs__item"
      >
        <i class="breadcrumbs__item__angle pi pi-angle-right"></i>
        <span
          class="breadcrumbs__item__text"
          tabindex="0"
          :aria-current="index === appState$$.breadCrumbs.length - 1 ? 'page' : null"
          @click="goTo(breadCrumb)"
          @keyup.enter="goTo(breadCrumb)"
        >
          {{ breadCrumb?.text || "" }}
        </span>
      </li>
    </ol>
  </nav>
</template>

<script setup>
// #region VUE
import {inject} from 'vue';
// #endregion

// #region STTEDRA
import {useDev} from '@sttedra_ui/framework';
// #endregion

import {useRouter} from 'vue-router';
import {useDebounceFn} from '@vueuse/core';

import Button from 'primevue/button/sfc';

// #region Params, Ref, Reactives, Computed
const appState$$ = inject('appState$$');
const router = useRouter();
// #endregion

// #region Functions

const goTo = useDebounceFn(goToFn, 200, {maxWait: 700});
async function goToFn(breadCrumb = {}) {
  try {
    if (breadCrumb?.route) {
      router.push({name: breadCrumb?.route || 'Home'});
    } else if (breadCrumb?.link) {
      window.open(breadCrumb?.link, '_self');
    }
  } catch (error) {
    useDev.error('[BreadCrumbs.vue] goToFn', error);
  }
}

const goToHome = useDebounceFn(goToHomeFn, 200, {maxWait: 700});
async function goToHomeFn() {
  try {
    window.open('https://www.transportes.gob.es/', '_self');
  } catch (error) {
    useDev.error('[BreadCrumbs.vue] goToHome', error);
  }
}
// #endregion
</script>

<style lang="scss" scoped>
@use "@/styles/vars" as *;
@use "@/styles/breakpoints" as *;

.breadcrumbs {
  display: flex;
  align-items: center;
  justify-content: flex-start;

  @include media("<=phone") {
    padding: $brand__boxspace--medium ($brand__boxspace--medium * 1)
      $brand__boxspace--medium ($brand__boxspace--medium * 1.5);
  }

  @include media(">phone") {
    padding: $brand__boxspace--medium ($brand__boxspace--medium * 2)
      $brand__boxspace--medium ($brand__boxspace--medium * 2.5);
  }

  @include media(">phone", "<=tablet") {
    padding: $brand__boxspace--medium ($brand__boxspace--medium * 3)
      $brand__boxspace--medium ($brand__boxspace--medium * 3.5);
  }

  @include media(">tablet", "<=1224px") {
    padding: $brand__boxspace--medium ($brand__boxspace--medium * 4)
      $brand__boxspace--medium ($brand__boxspace--medium * 4.5);
  }

  @include media(">1224px") {
    padding: $brand__boxspace--medium ($brand__boxspace--medium * 20)
      $brand__boxspace--medium ($brand__boxspace--medium * 20);
  }

  &__list {
    display: flex;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0;
     gap: .5rem;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: .5rem;

    &__angle {
      margin: 0 $brand__boxspace--small;

      @include media("<=tablet") {
        font-size: $pv__fs--xlarge--phone;
      }

      @include media(">tablet") {
        font-size: $pv__fs--large;
      }
    }

    &__text {
      background: none;
      border: none;
      font: inherit;
      padding: 0;
      cursor: pointer;
      color: inherit;

      @include media("<=tablet") {
        font-size: $pv__fs--xlarge--phone;
      }

      @include media(">tablet") {
        font-size: $pv__fs--large;
      }

      &:hover {
        color: #1d1d1b;
        opacity: 0.7;
        text-decoration: none;
      }

      &[aria-current="page"] {
        cursor: default;
      }

      &:focus {
        outline: 2px solid var(--focus-outline);
      }
    }

    &:last-child {

      span {
        color: var(--primary-color);
        cursor: default;

        &:hover {
          color: var(--primary-800);
          opacity: 1;
          cursor: pointer;
        }
      }

      @include media("<=tablet") {
        display: flex !important;
      }
    }

    @include media("<=tablet") {
      display: none !important;
    }
  }
}

.breadcrumbs__container:last-child .breadcrumbs__container__text {
  cursor: unset;
  &:hover {
    color: $pv__color--green-alter;
    opacity: 1;
  }
}
</style>
